import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  Button, DatePicker, Form, Input, Modal, Radio, Select, Space, Table, message,
} from 'antd';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import { AdminContext } from 'constants/contexts';
import dayjs from 'dayjs';
import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import {
  createCampaign,
  getCampaign,
  previewCampaignAudience,
  scheduleCampaign,
  sendCampaign,
  updateCampaign,
} from 'api/campaigns';
import { getSegments } from 'api/segments';
import { fetchCustomers } from 'api/customers';
import { getAllCoupons } from 'containers/Coupons/coupons';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { getCampaignErrorMessage } from './errorMessages';
import { StyledWrapper } from './styles';

function customerToOption(c) {
  return {
    value: c.id,
    label: `${c.name || 'Customer'} (${c.email || c.phone || c.id})`,
    customer: c,
  };
}

function extractListResults(res) {
  const root = getData(res)?.data || getData(res) || res;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.results)) return root.results;
  if (Array.isArray(root?.data?.results)) return root.data.results;
  return [];
}

function CampaignFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { organizationId } = useContext(AdminContext);
  const [form] = Form.useForm();
  const channel = Form.useWatch('channel', form) || 'email';
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [segments, setSegments] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customersSearching, setCustomersSearching] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [excludedRecipients, setExcludedRecipients] = useState([]);
  const [excludedCount, setExcludedCount] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [audienceError, setAudienceError] = useState('');
  const [scheduleMode, setScheduleMode] = useState('now');
  // Survives create→send failures so retries update the same draft instead of duplicating.
  const [persistedCampaignId, setPersistedCampaignId] = useState(id || null);
  const customerSearchSeq = React.useRef(0);

  const mergedCustomerOptions = useMemo(() => {
    const byId = new Map();
    selectedCustomers.forEach((c) => byId.set(c.id, customerToOption(c)));
    customerOptions.forEach((o) => byId.set(o.value, o));
    return Array.from(byId.values());
  }, [selectedCustomers, customerOptions]);

  useEffect(() => {
    setPersistedCampaignId(id || null);
  }, [id]);

  useEffect(() => {
    if (!organizationId) return undefined;
    let cancelled = false;

    const loadAllSegments = async () => {
      const all = [];
      let page = 1;
      // API max_page_size is 100 for the default paginator.
      while (!cancelled) {
        // eslint-disable-next-line no-await-in-loop
        const res = await getSegments({ page, page_size: 100 });
        const data = getData(res)?.data || getData(res);
        const results = data?.results || [];
        all.push(...results);
        if (!data?.next || results.length === 0) break;
        page += 1;
        if (page > 50) break;
      }
      if (!cancelled) setSegments(all);
    };

    const loadAllCoupons = async () => {
      const all = [];
      let page = 1;
      while (!cancelled) {
        // eslint-disable-next-line no-await-in-loop
        const data = await getAllCoupons('', page, false, organizationId, 100);
        const results = data?.results || (Array.isArray(data) ? data : []);
        all.push(...results);
        if (!data?.next || results.length === 0) break;
        page += 1;
        if (page > 50) break;
      }
      if (!cancelled) setCoupons(all);
    };

    loadAllSegments().catch(captureException);
    loadAllCoupons().catch((e) => {
      captureException(e);
      if (!cancelled) setCoupons([]);
    });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getCampaign(id)
      .then((res) => {
        const data = getData(res)?.data || getData(res);
        form.setFieldsValue({
          name: data.name,
          channel: data.channel,
          subject: data.subject,
          body: data.body,
          coupon: data.coupon,
          segment_ids: (data.segments || []).map((s) => s.id),
        });
        const extras = data.extra_customers || [];
        setSelectedCustomers(extras);
        setCustomerOptions(extras.map(customerToOption));
        setRecipientCount(data.recipient_count ?? null);
        setLoading(false);
      })
      .catch((e) => {
        captureException(e);
        message.error(getCampaignErrorMessage(e, 'Could not load this campaign.'));
        setLoading(false);
      });
  }, [id, isEdit, form]);

  const searchCustomers = useDebouncedCallback((value) => {
    if (!organizationId) {
      message.error('Organization not loaded. Refresh and try again.');
      return;
    }
    const q = (value || '').trim();
    if (q.length < 2) {
      setCustomerOptions([]);
      return;
    }
    const seq = ++customerSearchSeq.current;
    setCustomersSearching(true);
    fetchCustomers({ searchKey: q, page: 1 })
      .then((res) => {
        if (seq !== customerSearchSeq.current) return;
        const results = extractListResults(res);
        setCustomerOptions(results.map(customerToOption));
      })
      .catch((e) => {
        if (seq !== customerSearchSeq.current) return;
        captureException(e);
        message.error(getCampaignErrorMessage(e, 'Could not search customers. Try again.'));
        setCustomerOptions([]);
      })
      .finally(() => {
        if (seq === customerSearchSeq.current) setCustomersSearching(false);
      });
  }, 400);

  const buildPayload = useCallback((values) => ({
    name: values.name,
    channel: values.channel,
    subject: values.channel === 'email' ? values.subject : '',
    body: values.body,
    coupon: values.coupon || null,
    segment_ids: values.segment_ids || [],
    extra_customer_ids: selectedCustomers.map((c) => c.id),
  }), [selectedCustomers]);

  const hidePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewRecipients([]);
    setExcludedRecipients([]);
    setExcludedCount(0);
    setRecipientCount(null);
    setAudienceError('');
  }, []);

  const runPreview = useCallback(async (values) => {
    setAudienceError('');
    setPreviewLoading(true);
    try {
      const res = await previewCampaignAudience({
        channel: values.channel,
        segment_ids: values.segment_ids || [],
        extra_customer_ids: selectedCustomers.map((c) => c.id),
      });
      const data = getData(res)?.data || getData(res);
      const reachable = data?.recipient_count ?? 0;
      const excluded = data?.excluded_count ?? 0;
      setRecipientCount(reachable);
      setPreviewRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
      setExcludedRecipients(Array.isArray(data?.excluded) ? data.excluded : []);
      setExcludedCount(excluded);
      if (reachable === 0 && excluded > 0) {
        const contact = (values.channel || channel) === 'sms' ? 'phone number' : 'email address';
        setAudienceError(`Nobody in this audience has a ${contact}. ${excluded} customer(s) will be skipped.`);
      }
      return { ok: true, reachable, excluded };
    } catch (e) {
      const detail = getCampaignErrorMessage(e, 'Could not preview recipients.');
      setAudienceError(detail);
      setRecipientCount(null);
      setPreviewRecipients([]);
      setExcludedRecipients([]);
      setExcludedCount(0);
      setPreviewOpen(false);
      return { ok: false, reachable: 0, excluded: 0 };
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedCustomers, channel]);

  const saveDraft = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = buildPayload(values);
      const campaignId = persistedCampaignId || id;
      if (campaignId) {
        await updateCampaign(campaignId, payload);
        message.success('Draft saved');
        navigate(`/console/marketing/campaigns/${campaignId}`);
      } else {
        const res = await createCampaign(payload);
        const data = getData(res)?.data || getData(res);
        setPersistedCampaignId(data.id);
        message.success('Draft created');
        navigate(`/console/marketing/campaigns/${data.id}`);
      }
    } catch (e) {
      if (e?.errorFields) return;
      captureException(e);
      message.error(getCampaignErrorMessage(e, 'Could not save draft.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmSend = ({
    reachable, excluded, channel: sendChannel, sendAt,
  }) => new Promise((resolve) => {
    const isSms = sendChannel === 'sms';
    const label = isSms ? 'SMS' : 'email';
    const people = (n) => `${n} customer${n === 1 ? '' : 's'}`;
    const title = sendAt
      ? `Schedule this ${label} for ${dayjs(sendAt).format('MMM D, h:mm A')} to ${people(reachable)}?`
      : `Send this ${label} to ${people(reachable)}?`;
    const skippedNote = excluded > 0
      ? `${people(excluded)} will be skipped because they don\u2019t have ${isSms ? 'a phone number' : 'an email address'}. Everyone else will still get it.`
      : null;
    Modal.confirm({
      title,
      content: (
        <>
          {skippedNote && <p>{skippedNote}</p>}
          <p style={{ marginBottom: 0 }}>
            {sendAt
              ? 'Recipients are checked again when it sends.'
              : 'This can\u2019t be undone.'}
          </p>
        </>
      ),
      okText: sendAt ? 'Schedule' : 'Send now',
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

  const saveAndSend = async () => {
    try {
      const values = await form.validateFields();
      if (scheduleMode === 'later' && !values.send_at) {
        message.error('Pick a schedule time');
        return;
      }
      setSending(true);
      const preview = await runPreview(values);
      if (!preview.ok || !preview.reachable) {
        if (preview.ok && !preview.reachable) {
          message.error('Add at least one recipient who can be reached on this channel.');
        }
        setSending(false);
        return;
      }
      const confirmed = await confirmSend({
        reachable: preview.reachable,
        excluded: preview.excluded,
        channel: values.channel || channel,
        sendAt: scheduleMode === 'later' ? values.send_at : null,
      });
      if (!confirmed) {
        setSending(false);
        return;
      }
      const payload = buildPayload(values);
      let campaignId = persistedCampaignId || id;
      if (campaignId) {
        await updateCampaign(campaignId, payload);
      } else {
        const res = await createCampaign(payload);
        const data = getData(res)?.data || getData(res);
        campaignId = data.id;
        setPersistedCampaignId(campaignId);
        // So a retry after a failed send updates this draft instead of creating another.
        navigate(`/console/marketing/campaigns/${campaignId}/edit`, { replace: true });
      }
      if (scheduleMode === 'later') {
        const sendAt = dayjs(values.send_at).toISOString();
        await scheduleCampaign(campaignId, sendAt);
        message.success('Campaign scheduled');
      } else {
        await sendCampaign(campaignId);
        message.success('Campaign send started');
      }
      navigate(`/console/marketing/campaigns/${campaignId}`);
    } catch (e) {
      if (e?.errorFields) return;
      captureException(e);
      message.error(getCampaignErrorMessage(
        e,
        scheduleMode === 'later' ? 'Could not schedule campaign.' : 'Could not send campaign.',
      ));
    } finally {
      setSending(false);
    }
  };

  const couponHint = useMemo(
    () => 'If you pick a coupon, put {{coupon_code}} where it should appear, or the code is added at the end automatically.',
    [],
  );

  if (loading) {
    return (
      <Layout>
        <StyledWrapper>
          <p>Loading…</p>
        </StyledWrapper>
      </Layout>
    );
  }

  return (
    <Layout>
      <StyledWrapper>
        <Button
          type="link"
          className="back-link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/console/marketing/campaigns')}
        >
          Back to campaigns
        </Button>
        <div className="campaigns-header">
          <div>
            <h1 className="campaigns-title">{isEdit ? 'Edit campaign' : 'Create campaign'}</h1>
            <p className="campaigns-subtitle">Draft first, then send now or schedule</p>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ channel: 'email', segment_ids: [] }}
          style={{ maxWidth: 720 }}
        >
          <div className="form-section">
            <Form.Item
              name="name"
              label="Campaign name"
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input placeholder="Spring re-engagement" />
            </Form.Item>

            <Form.Item name="channel" label="Channel" rules={[{ required: true }]}>
              <Radio.Group>
                <Radio.Button value="email">Email</Radio.Button>
                <Radio.Button value="sms">SMS</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {channel === 'email' && (
              <Form.Item
                name="subject"
                label="Subject"
                rules={[{ required: true, message: 'Subject is required for email' }]}
              >
                <Input placeholder="We miss you" />
              </Form.Item>
            )}

            <Form.Item
              name="body"
              label={channel === 'email' ? 'Message (plain text)' : 'SMS message'}
              rules={[{ required: true, message: 'Message is required' }]}
              extra={couponHint}
            >
              <Input.TextArea rows={6} placeholder="Write your message…" />
            </Form.Item>

            <Form.Item name="coupon" label="Coupon (optional)">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select an existing coupon"
                options={coupons.map((c) => ({
                  value: c.id,
                  label: `${c.name} (${c.code})`,
                }))}
              />
            </Form.Item>
          </div>

          <div className="form-section">
            <h3>Audience</h3>
            <Form.Item
              name="segment_ids"
              label="Segments"
              extra="Customers in a segment who are missing an email or phone are skipped for that channel. Everyone else is still included."
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select segments"
                options={segments.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>

            <Form.Item label="Individual customers (search by email)">
              <Select
                mode="multiple"
                showSearch
                filterOption={false}
                placeholder="Type at least 2 characters to search"
                options={mergedCustomerOptions}
                value={selectedCustomers.map((c) => c.id)}
                loading={customersSearching}
                notFoundContent={
                  customersSearching
                    ? 'Searching…'
                    : 'Type an email or name (min 2 characters)'
                }
                onSearch={searchCustomers}
                onChange={(ids) => {
                  const byId = new Map([
                    ...selectedCustomers.map((c) => [c.id, c]),
                    ...mergedCustomerOptions
                      .filter((o) => o.customer)
                      .map((o) => [o.value, o.customer]),
                  ]);
                  setSelectedCustomers(
                    ids.map((cid) => byId.get(cid) || { id: cid, name: String(cid) }),
                  );
                }}
              />
            </Form.Item>

            <Space>
              <Button
                loading={previewLoading}
                onClick={async () => {
                  if (previewOpen) {
                    hidePreview();
                    return;
                  }
                  const values = await form.validateFields(['channel', 'segment_ids']).catch(() => form.getFieldsValue());
                  const preview = await runPreview({
                    channel: values.channel || channel,
                    segment_ids: values.segment_ids || [],
                  });
                  if (preview.ok) setPreviewOpen(true);
                }}
              >
                {previewOpen ? 'Hide recipients' : 'Preview recipients'}
              </Button>
              {previewOpen && recipientCount != null && (
                <span className="recipient-count">
                  {recipientCount}
                  {' '}
                  will receive this
                  {' '}
                  {channel === 'sms' ? 'SMS' : 'email'}
                  {excludedCount > 0 ? ` · ${excludedCount} skipped` : ''}
                  {recipientCount > previewRecipients.length && previewRecipients.length > 0
                    ? ` (showing first ${previewRecipients.length})`
                    : ''}
                </span>
              )}
            </Space>
            {audienceError && <div className={recipientCount ? 'recipient-warning' : 'recipient-error'}>{audienceError}</div>}
            {previewOpen && previewRecipients.length > 0 && (
              <Table
                style={{ marginTop: 16 }}
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={previewRecipients}
                columns={[
                  { title: 'Name', dataIndex: 'name', render: (v) => v || '—' },
                  { title: 'Email', dataIndex: 'email', render: (v) => v || '—' },
                  { title: 'Phone', dataIndex: 'phone', render: (v) => v || '—' },
                ]}
              />
            )}
            {previewOpen && excludedCount > 0 && (
              <>
                <div className="recipient-warning" style={{ marginTop: 16 }}>
                  Skipped — missing
                  {' '}
                  {channel === 'sms' ? 'a phone number' : 'an email'}
                  {excludedCount > excludedRecipients.length
                    ? ` (showing first ${excludedRecipients.length})`
                    : ''}
                </div>
                <Table
                  style={{ marginTop: 8 }}
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={excludedRecipients}
                  columns={[
                    { title: 'Name', dataIndex: 'name', render: (v) => v || '—' },
                    { title: 'Email', dataIndex: 'email', render: (v) => v || '—' },
                    { title: 'Phone', dataIndex: 'phone', render: (v) => v || '—' },
                    { title: 'Reason', dataIndex: 'reason', render: (v) => v || '—' },
                  ]}
                />
              </>
            )}
          </div>

          <div className="form-section">
            <h3>Send</h3>
            <Radio.Group
              value={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.value)}
              style={{ marginBottom: 12 }}
            >
              <Radio value="now">Send now</Radio>
              <Radio value="later">Schedule</Radio>
            </Radio.Group>
            {scheduleMode === 'later' && (
              <Form.Item
                name="send_at"
                label="Send at"
                rules={[{ required: true, message: 'Schedule time is required' }]}
              >
                <DatePicker
                  showTime={{
                    use12Hours: true,
                    format: 'h:mm A',
                    showSecond: false,
                  }}
                  format="MMM D, YYYY h:mm A"
                  style={{ width: '100%' }}
                  disabledDate={(d) => d && d.isBefore(dayjs(), 'day')}
                />
              </Form.Item>
            )}
          </div>

          <Space>
            <Button onClick={() => navigate('/console/marketing/campaigns')} disabled={saving || sending}>
              Cancel
            </Button>
            <Button loading={saving} disabled={sending} onClick={saveDraft}>Save draft</Button>
            <Button type="primary" loading={sending} disabled={saving} onClick={saveAndSend}>
              {scheduleMode === 'later' ? 'Schedule' : 'Send now'}
            </Button>
          </Space>
        </Form>
      </StyledWrapper>
    </Layout>
  );
}

export default withGuard(CampaignFormPage, [PERMISSIONS.MARKETING], { redirect: true });
